import { useState, useEffect, useCallback } from 'react';
import { Clock, Music, LogIn } from 'lucide-react';
import { useJukeboxCliente } from '../contexts/JukeboxClienteContext';
import { buscarHistorico } from '../../data/jukeboxClienteService';
import type { HistoricoItem } from '../../domain/entities/PerfilJukebox';
import { getClientePrimeiroNome } from '../../domain/usecases/clienteDisplay';

export default function JukeboxHistoricoPage() {
  const { status, cliente, openLogin } = useJukeboxCliente();
  const [items, setItems] = useState<HistoricoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  const fetchHistorico = useCallback(async (off: number) => {
    if (!cliente) return;
    setLoading(true);
    try {
      const result = await buscarHistorico(cliente.token, off);
      if (result) {
        setItems(prev => off === 0 ? result.items : [...prev, ...result.items]);
        setTotal(result.total);
      }
    } finally {
      setLoading(false);
    }
  }, [cliente]);

  useEffect(() => {
    if (status === 'authenticated') {
      setOffset(0);
      fetchHistorico(0);
    }
  }, [status, fetchHistorico]);

  const loadMore = () => {
    const newOffset = offset + 20;
    setOffset(newOffset);
    fetchHistorico(newOffset);
  };

  if (status === 'anonymous') {
    return (
      <div className="min-h-dvh bg-jukebox-bg pt-[env(safe-area-inset-top)] pb-24 px-4 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-xs">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-orange-500/10 flex items-center justify-center">
            <Clock size={28} className="text-orange-400" />
          </div>
          <h2 className="text-lg font-bold text-white">Seu Histórico</h2>
          <p className="text-sm text-white/50">
            Faça login para ver todas as músicas que você pediu, mesmo em visitas anteriores.
          </p>
          <button
            onClick={openLogin}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 text-white font-semibold"
          >
            <LogIn size={18} />
            Entrar com WhatsApp
          </button>
        </div>
      </div>
    );
  }

  const primeiroNome = getClientePrimeiroNome(cliente?.nome);

  return (
    <div className="min-h-dvh bg-jukebox-bg pt-[env(safe-area-inset-top)] pb-24">
      <div className="sticky top-0 z-20 border-b border-white/[0.05] bg-jukebox-bg/90 px-4 py-3 backdrop-blur-xl pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <p className="text-[11px] font-medium text-white/50">Seus pedidos</p>
        <h1 className="text-base font-bold tracking-tight text-white">Histórico</h1>
        <p className="mt-0.5 text-xs text-white/55">
          {primeiroNome
            ? `Setlist de ${primeiroNome}`
            : `${total} ${total === 1 ? 'música' : 'músicas'} pedidas`}
        </p>
      </div>

      {loading && items.length === 0 ? (
        <div className="px-4 pt-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] animate-pulse">
              <div className="w-12 h-12 rounded-lg bg-white/[0.06]" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-3/4 bg-white/[0.06] rounded-full" />
                <div className="h-2 w-1/2 bg-white/[0.04] rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 pt-16 text-center space-y-3">
          <Music size={40} className="mx-auto text-white/30" />
          <p className="text-sm text-white/55">Nenhum pedido ainda</p>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-2">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/[0.05]">
              <img
                src={item.thumbnail}
                alt=""
                loading="lazy"
                className="w-12 h-12 rounded-lg object-cover"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{item.titulo}</p>
                <p className="text-xs text-white/55">
                  {new Date(item.criado_em).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <div className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                item.status === 'concluido' ? 'bg-emerald-500/10 text-emerald-400'
                  : item.status === 'tocando' ? 'bg-orange-500/10 text-orange-400'
                  : 'bg-white/[0.06] text-white/40'
              }`}>
                {item.status === 'concluido' ? 'Tocada' : item.status === 'tocando' ? 'Tocando' : 'Pendente'}
              </div>
            </div>
          ))}

          {items.length < total && (
            <button
              onClick={loadMore}
              disabled={loading}
              className="w-full py-3 text-sm text-white/40 hover:text-white/60 transition-colors"
            >
              {loading ? 'Carregando...' : 'Carregar mais'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
